import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FileEarmarkText, FolderFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { listStoreDocuments } from '../../../services/storeDocumentsApi';
import '../../../assets/styles/files.css';

const StoreFiles = () => {
  const navigate = useNavigate();
  const [documentGroups, setDocumentGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const normalizeDocumentGroups = useCallback((data = []) => {
    if (!Array.isArray(data)) return [];
    return data.map((group) => ({
      ...group,
      documents: Array.isArray(group.documents) ? group.documents : [],
    }));
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const { data } = await listStoreDocuments();
      setDocumentGroups(normalizeDocumentGroups(data));
    } catch (err) {
      setError(err.message || 'Failed to load files.');
      setDocumentGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, [normalizeDocumentGroups]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const folderCards = useMemo(() => {
    return [...documentGroups]
      .map((group) => ({
        id: group.fileNameId ?? group.formName,
        formName: group.formName,
        count: group.documents.length,
      }))
      .sort((first, second) => first.formName.localeCompare(second.formName));
  }, [documentGroups]);

  const handleOpenFolder = useCallback(
    (folderId, formName) => {
      if (!folderId && !formName) return;
      navigate(`/store/files/${encodeURIComponent(folderId || formName)}`, { state: { formName } });
    },
    [navigate],
  );

  return (
    <div className="files-page container-fluid py-4">
      <div className="card files-table-card shadow-sm">
        <div className="card-body">
          {isLoading ? (
            <div className="py-5 text-center"><Spinner animation="border" role="status" /></div>
          ) : error ? (
            <div className="py-5 px-3"><Alert variant="danger" className="mb-0">{error}</Alert></div>
          ) : folderCards.length === 0 ? (
            <div className="files-empty-state text-center py-5">
              <div className="files-empty-icon d-inline-flex align-items-center justify-content-center mb-3"><FileEarmarkText size={28} /></div>
              <p className="mb-1 fw-semibold">No documents found.</p>
              <small className="text-muted">No files have been uploaded for your store yet.</small>
            </div>
          ) : (
            <div className="files-folder-grid">
              {folderCards.map((folder) => (
                <button key={folder.id} type="button" className="files-folder-card open text-start" onClick={() => handleOpenFolder(folder.id, folder.formName)}>
                  <div className="files-folder-header"><div className="files-folder-title"><div className="files-folder-icon"><FolderFill size={26} /></div><div><p className="mb-0 fw-semibold text-capitalize">{folder.formName}</p><small className="text-muted">{folder.count} {folder.count === 1 ? 'file' : 'files'}</small></div></div></div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreFiles;
